import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, List, Table2 } from 'lucide-react';

const PrintModal = ({ selectedClients, staffList, onClose }) => {
    const [format, setFormat] = useState('detailed'); // 'detailed' or 'table'
    const printRef = useRef(null);

    const getStaffName = (staffId) => {
        if (!staffId) return 'Unassigned';
        const staff = staffList.find(s => s.id === staffId);
        return staff ? staff.name : 'Unknown';
    };

    const printHtmlContent = (htmlContent) => {
        const printWindow = window.open('', '_blank', 'height=800,width=1200');

        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(htmlContent);
            printWindow.document.close();

            const triggerPrint = () => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            };

            if (printWindow.document.readyState === 'complete') {
                setTimeout(triggerPrint, 150);
            } else {
                printWindow.onload = () => setTimeout(triggerPrint, 150);
            }

            return;
        }

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);

        const cleanup = () => {
            setTimeout(() => {
                if (iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                }
            }, 500);
        };

        iframe.onload = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } finally {
                cleanup();
            }
        };

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();
    };

    const handlePrint = () => {
        let htmlContent = '';
        
        if (format === 'table') {
            // Table format
            htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <title>Client List Report</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 15px;
                            color: #333;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #0066cc;
                            padding-bottom: 10px;
                        }
                        .header h1 {
                            margin: 0;
                            color: #0066cc;
                            font-size: 20px;
                        }
                        .header p {
                            margin: 5px 0;
                            font-size: 11px;
                            color: #666;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                            font-size: 11px;
                        }
                        th {
                            background-color: #0066cc;
                            color: white;
                            padding: 8px;
                            text-align: left;
                            font-weight: bold;
                            border: 1px solid #0066cc;
                        }
                        td {
                            padding: 6px 8px;
                            border: 1px solid #ddd;
                        }
                        tr:nth-child(even) {
                            background-color: #f9f9f9;
                        }
                        tr:hover {
                            background-color: #f0f0f0;
                        }
                        .notes-cell {
                            max-width: 200px;
                            white-space: pre-wrap;
                            word-wrap: break-word;
                        }
                        @media print {
                            body { margin: 10px; }
                            table { font-size: 10px; }
                            th, td { padding: 4px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Client List Report</h1>
                        <p>Generated: ${new Date().toLocaleString('en-GB')}</p>
                        <p>Total Clients: ${selectedClients.length}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Number</th>
                                <th>Acc Due</th>
                                <th>Tax Due</th>
                                <th>CS Due</th>
                                <th>Work Plan</th>
                                <th>Status (Acc)</th>
                                <th>Status (Tax)</th>
                                <th>Status (CS)</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${selectedClients.map(client => `
                                <tr>
                                    <td><strong>${client.name || 'Unknown'}</strong></td>
                                    <td>${client.number || '-'}</td>
                                    <td>${client.accountsDue ? new Date(client.accountsDue).toLocaleDateString('en-GB') : '-'}</td>
                                    <td>${client.ct600Due ? new Date(client.ct600Due).toLocaleDateString('en-GB') : '-'}</td>
                                    <td>${client.csDue ? new Date(client.csDue).toLocaleDateString('en-GB') : '-'}</td>
                                    <td>${client.plannedMonth || 'Unscheduled'}</td>
                                    <td>${client.accountsStatus || '-'}</td>
                                    <td>${client.taxStatus || '-'}</td>
                                    <td>${client.csStatus || '-'}</td>
                                    <td class="notes-cell">${client.notes ? client.notes.substring(0, 100) + (client.notes.length > 100 ? '...' : '') : '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </body>
                </html>
            `;
        } else {
            // Detailed format (original)
            htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <title>Client Details Report</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                            color: #333;
                        }
                        .header {
                            text-align: center;
                            margin-bottom: 20px;
                            border-bottom: 2px solid #0066cc;
                            padding-bottom: 10px;
                        }
                        .header h1 {
                            margin: 0;
                            color: #0066cc;
                        }
                        .header p {
                            margin: 5px 0;
                            font-size: 12px;
                            color: #666;
                        }
                        .client-block {
                            margin-bottom: 20px;
                            border: 1px solid #ddd;
                            padding: 15px;
                            page-break-inside: avoid;
                        }
                        .client-header {
                            background-color: #f5f5f5;
                            padding: 10px;
                            margin: -15px -15px 15px -15px;
                            border-bottom: 2px solid #0066cc;
                        }
                        .client-name {
                            font-size: 16px;
                            font-weight: bold;
                            color: #0066cc;
                            margin: 0;
                        }
                        .client-number {
                            font-size: 12px;
                            color: #666;
                            margin: 5px 0 0 0;
                        }
                        .client-grid {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 15px;
                            margin-bottom: 15px;
                        }
                        .client-field {
                            font-size: 12px;
                        }
                        .field-label {
                            font-weight: bold;
                            color: #666;
                            font-size: 11px;
                            text-transform: uppercase;
                        }
                        .field-value {
                            color: #333;
                            font-size: 12px;
                            margin-top: 3px;
                        }
                        .notes-section {
                            margin-top: 15px;
                            padding-top: 15px;
                            border-top: 1px solid #eee;
                        }
                        .notes-label {
                            font-weight: bold;
                            color: #666;
                            font-size: 11px;
                            text-transform: uppercase;
                        }
                        .notes-value {
                            font-size: 12px;
                            color: #333;
                            white-space: pre-wrap;
                            margin-top: 5px;
                            line-height: 1.4;
                        }
                        @media print {
                            body { margin: 10px; }
                            .client-block { page-break-inside: avoid; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Client Details Report</h1>
                        <p>Generated: ${new Date().toLocaleString('en-GB')}</p>
                        <p>Total Clients: ${selectedClients.length}</p>
                    </div>
                    ${selectedClients.map(client => `
                        <div class="client-block">
                            <div class="client-header">
                                <p class="client-name">${client.name || 'Unknown'}</p>
                                <p class="client-number">${client.number || '-'}</p>
                            </div>
                            <div class="client-grid">
                                <div class="client-field">
                                    <div class="field-label">Accounts Due</div>
                                    <div class="field-value">${client.accountsDue ? new Date(client.accountsDue).toLocaleDateString('en-GB') : '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Tax Due</div>
                                    <div class="field-value">${client.ct600Due ? new Date(client.ct600Due).toLocaleDateString('en-GB') : '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">CS Due</div>
                                    <div class="field-value">${client.csDue ? new Date(client.csDue).toLocaleDateString('en-GB') : '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Work Plan</div>
                                    <div class="field-value">${client.plannedMonth || 'Unscheduled'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Status (Acc)</div>
                                    <div class="field-value">${client.accountsStatus || '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Status (Tax)</div>
                                    <div class="field-value">${client.taxStatus || '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Status (CS)</div>
                                    <div class="field-value">${client.csStatus || '-'}</div>
                                </div>
                                <div class="client-field">
                                    <div class="field-label">Staff (Acc)</div>
                                    <div class="field-value">${getStaffName(client.assignedAcc)}</div>
                                </div>
                            </div>
                            ${client.notes ? `
                                <div class="notes-section">
                                    <div class="notes-label">Notes</div>
                                    <div class="notes-value">${client.notes}</div>
                                </div>
                            ` : ''}
                        </div>
                    `).join('')}
                </body>
                </html>
            `;
        }
        
        printHtmlContent(htmlContent);
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-auto flex flex-col">
                {/* Header */}
                <div className="bg-blue-600 text-white p-4 flex items-center justify-between sticky top-0 z-10">
                    <div className="flex items-center gap-2">
                        <Printer className="w-5 h-5" />
                        <h2 className="text-lg font-bold">Print Report</h2>
                    </div>
                    <button onClick={onClose} className="hover:bg-blue-700 p-1 rounded transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Format Selector */}
                <div className="bg-white border-b border-slate-200 p-4 flex items-center gap-4">
                    <span className="text-sm font-semibold text-slate-700">Format:</span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFormat('detailed')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                format === 'detailed'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                        >
                            <List className="w-4 h-4" />
                            Detailed
                        </button>
                        <button
                            onClick={() => setFormat('table')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                format === 'table'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                        >
                            <Table2 className="w-4 h-4" />
                            Table
                        </button>
                    </div>
                </div>

                {/* Preview */}
                <div className="flex-1 overflow-auto p-6 bg-slate-50">
                    <div ref={printRef} className="bg-white p-8">
                        {format === 'detailed' ? (
                            <>
                                <div className="text-center mb-6 border-b-2 border-blue-600 pb-4">
                                    <h1 className="text-2xl font-bold text-blue-600 mb-2">Client Details Report</h1>
                                    <p className="text-sm text-gray-600">Generated: {new Date().toLocaleString('en-GB')}</p>
                                    <p className="text-sm text-gray-600">Total Clients: {selectedClients.length}</p>
                                </div>

                                {selectedClients.map(client => (
                                    <div key={client.id} className="mb-6 border border-gray-200 p-4">
                                        <div className="bg-gray-100 -m-4 mb-4 p-3 border-b-2 border-blue-600">
                                            <h3 className="text-base font-bold text-blue-600">{client.name || 'Unknown'}</h3>
                                            <p className="text-sm text-gray-600">{client.number || '-'}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Accounts Due</div>
                                                <div className="text-gray-900">{client.accountsDue ? new Date(client.accountsDue).toLocaleDateString('en-GB') : '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Tax Due</div>
                                                <div className="text-gray-900">{client.ct600Due ? new Date(client.ct600Due).toLocaleDateString('en-GB') : '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">CS Due</div>
                                                <div className="text-gray-900">{client.csDue ? new Date(client.csDue).toLocaleDateString('en-GB') : '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Work Plan</div>
                                                <div className="text-gray-900">{client.plannedMonth || 'Unscheduled'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Status (Acc)</div>
                                                <div className="text-gray-900">{client.accountsStatus || '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Status (Tax)</div>
                                                <div className="text-gray-900">{client.taxStatus || '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Status (CS)</div>
                                                <div className="text-gray-900">{client.csStatus || '-'}</div>
                                            </div>
                                            <div className="text-sm">
                                                <div className="font-bold text-gray-700 text-xs uppercase">Staff (Acc)</div>
                                                <div className="text-gray-900">{getStaffName(client.assignedAcc)}</div>
                                            </div>
                                        </div>

                                        {client.notes && (
                                            <div className="border-t pt-4">
                                                <div className="font-bold text-gray-700 text-xs uppercase mb-2">Notes</div>
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">{client.notes}</div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <>
                                <div className="text-center mb-6 border-b-2 border-blue-600 pb-4">
                                    <h1 className="text-2xl font-bold text-blue-600 mb-2">Client List Report</h1>
                                    <p className="text-sm text-gray-600">Generated: {new Date().toLocaleString('en-GB')}</p>
                                    <p className="text-sm text-gray-600">Total Clients: {selectedClients.length}</p>
                                </div>

                                <table className="w-full text-xs border-collapse border border-gray-300">
                                    <thead>
                                        <tr className="bg-blue-600 text-white">
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Company</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Number</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Acc Due</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Tax Due</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">CS Due</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Work Plan</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Status (Acc)</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Status (Tax)</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Status (CS)</th>
                                            <th className="border border-gray-300 px-3 py-2 text-left font-bold">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedClients.map((client, idx) => (
                                            <tr key={client.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-900 font-medium">{client.name}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.number || '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.accountsDue ? new Date(client.accountsDue).toLocaleDateString('en-GB') : '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.ct600Due ? new Date(client.ct600Due).toLocaleDateString('en-GB') : '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.csDue ? new Date(client.csDue).toLocaleDateString('en-GB') : '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.plannedMonth || 'Unscheduled'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.accountsStatus || '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.taxStatus || '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700">{client.csStatus || '-'}</td>
                                                <td className="border border-gray-300 px-3 py-2 text-gray-700 text-xs max-w-[120px] truncate">{client.notes ? client.notes.substring(0, 100) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t bg-white p-4 flex gap-3 justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
                    >
                        <Printer className="w-4 h-4" />
                        Print Report
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PrintModal;
